import moment from 'moment';
import CryptoJS from 'crypto-js';
import {
  format,
  isToday,
  isYesterday,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  getMonth,
  getYear,
  isSunday,
  isSaturday,
  isThisWeek,
  addDays,
  startOfWeek,
  subDays,
} from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { Product } from 'screens/Home/LandingPage/typing';

import store from '../redux/store';

import { EnumProduct, permissionEnum } from './typing';
import { EMI_PAYMENT_MODES, PATH_TO_PRODUCT_ID_MAP, PG_EDGE_ID, PLURAL_ID, PAYOUT_ID, PRODUCTS, PRODUCT_ID_TO_NAME_MAP, SUBSCRIPTION_ID, UiRoutes } from './constants';
import { Options } from 'components/SideBar/sidebar.constant';
import notify from './notifiy';

export const encryptUsingAES256 = (
  plainText: string,
  cryptoKey: string
): any => {
  const key = CryptoJS.enc.Utf8.parse(cryptoKey);
  const iv = CryptoJS.enc.Utf8.parse(cryptoKey);

  const encrypted = CryptoJS.AES.encrypt(
    CryptoJS.enc.Utf8.parse(plainText),
    key,
    {
      keySize: 128 / 8,
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    }
  ).toString();
  return encrypted;
};

export const decryptUsingAES256 = (
  encryptedText: string,
  cryptoKey: string
): string => {
  try {
    const key = CryptoJS.enc.Utf8.parse(cryptoKey);
    const iv = CryptoJS.enc.Utf8.parse(cryptoKey);

    let decrypted: any = CryptoJS.AES.decrypt(encryptedText, key, {
      keySize: 128 / 8,
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    decrypted = decrypted.toString(CryptoJS.enc.Utf8);

    return decrypted;
  } catch (err) {
    return '';
  }
};


export const getUtmSourceFromQuery = (
  query: Record<string, unknown> | undefined | null
): string => {
  const raw = query?.['utm_source'] ?? query?.['utmsource'] ?? query?.['utmSource'] ?? '';
  return String(raw).replace(/^"|"$/g, '').trim();
};

/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export const toPascal = (
  str: string,
  customSeperator = ' ',
  lowercaseRestElements = true
): string => {
  if (!str) {
    return 'NA';
  }
  const arr = str.split(customSeperator);
  const newStrArr: Array<string> = [];
  arr.map((stringWords: string) => {
    if (stringWords) {
      newStrArr.push(
        stringWords.slice(0, 1).toUpperCase() +
        (lowercaseRestElements
          ? stringWords.slice(1).toLowerCase()
          : stringWords.slice(1))
      );
    }
  });
  return newStrArr.join(' ');
};

export const toTitleCase = (str: string): string => {
  if (!str) {
    return 'NA';
  }
  // Replace underscores and hyphens with spaces
  const normalized = str.replace(/[_-]/g, ' ');
  // Split by spaces and capitalize first letter of each word
  return normalized
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const formatPaymentMethod = (str: string): string => {
  if (!str) {
    return 'NA';
  }
  if (str === "PAYMENT_METHOD_UPI" || str === "Upi" || str === "UPI") {
    return "UPI";
  }
  else {
    return toPascal(str.replace(/_/g, ' '));
  }
};

export const toStatusClass = (str: string): string => {
  return str.toLowerCase().replace(' ', '-');
};

export const toCapitalize = (str: string): string => {
  return str[0].toUpperCase() + str.substr(1).toLowerCase();
};

export const convertByteToKiloByte = (sizeInByte: number): number => {
  return Math.ceil(sizeInByte / 1000);
};

export const convertSecondsToMilliSeconds = (seconds: number): number => {
  return Math.ceil(seconds * 1000);
};

export const getMerchantIdFromStore = (): string | number => {
  return store.getState().loginReducer.loginState.MerchantId;
  // return 3413;
};

export const getUserEmailIdFromStore = (): string | number => {
  return store.getState().loginReducer.loginState.UserEmail;
};

export const getDashboardThemeDetails = (merchantOdsDataState: any) => {
  return merchantOdsDataState?.data?.[4] ?? {};
};

export const getMerchantDetailsFromStore = (): {
  merchantId: string | number;
  merchantName: string;
  merchantEmailId: string;
  merchantPan: string;
  merchantUserName: string;
  entityType: string;
  virtualStoreId: string;
  leadId: string;
  onboardingSource: string | null;
} => {
  return {
    merchantId: store.getState().loginReducer.loginState.MerchantId,
    merchantName: store.getState().loginReducer.loginState.merchantName,
    merchantEmailId: store.getState().loginReducer.loginState.UserEmail,
    merchantPan: decryptUsingAES256(
      store.getState().loginReducer.loginState.merchantPan,
      store.getState().loginReducer.loginState.leadId
    ),
    merchantUserName: store.getState().loginReducer.loginState.UserFullName,
    entityType: store.getState().loginReducer.loginState.entityType,
    virtualStoreId: store.getState().loginReducer.loginState.virtualStoreId,
    leadId: store.getState().loginReducer.loginState.leadId,
    onboardingSource: store.getState().loginReducer.loginState.onboardingSource,
  };
};

export const getIsAdminFromStore = (): boolean => {
  return store.getState().loginReducer.loginState.isAdmin;
};

export const getIsAdapterFromStore = (): boolean => {
  return store.getState().loginReducer.loginState.isAdapter;
};

export const getMerchnatVersionFromStore = (): string => {
  return store.getState().loginReducer.loginState.merchantVersion;
};

export const getCheckoutVersionFromStore = (): string | null => {
  return store.getState().loginReducer.loginState.checkoutVersion;
};

export const getIsPartnerMerchantFromStore = (): boolean => {
  return store.getState().loginReducer.loginState.isPartnerMerchant;
};


/**
 * @param gatewaysList list of all gateways for the merchant
 * @returns gateways options,
 * to get rid of conficts between gateways & status checkbox ids in filter
 */
export const paymentGatewayOptions = (gatewaysList) => {
  const gatewayOption: { id: string; label: string }[] = [];
  gatewaysList.forEach((item) =>
    gatewayOption.push({
      id: `${item.gatewayName}-${item.gatewayID}`,
      label: item.gatewayName,
    })
  );
  return gatewayOption;
};

export const findGatewayNameUsingGatewayId = (id, allGatewaysList) => {
  const gatewayId = id.split('-').pop();
  const gatewayObj = allGatewaysList.find(
    (item) => item.gatewayID.toString() === gatewayId.toString()
  );
  return gatewayObj && gatewayObj.gatewayName;
};

export const getGatewayName = (id: string): string => {
  return id.split('-')[0];
};

export const getGatewayId = (id: string): string => {
  return id.split('-')[1];
};

/**
 * @param orderStatusList list of all type of order status for the merchant
 * @returns order status options,
 * to get rid of conficts between gateways & status checkbox ids in filter
 */
export const orderStatusOptions = (orderStatusList) => {
  const orderStatusOption: { id: string; label: string }[] = [];
  orderStatusList
    .filter((orderStatus) => Boolean(orderStatus?.statusName))
    .forEach((item) => {
      orderStatusOption.push({
        id: `${item.statusName}-${item.statusId}`,
        label: item.statusName,
      });
    });
  return orderStatusOption;
};

export const getProfileImgText = (name) => {
  const namesArray = name.trim().split(' ');
  if (namesArray.length === 1) return `${namesArray[0].charAt(0)}`;
  else
    return `${namesArray[0].charAt(0)}${namesArray[
      namesArray.length - 1
    ].charAt(0)}`;
};
// fullName
//   .split(' ')
//   .map((name) => [name[0], name[1]])
//   .join(' ')
//   .toUpperCase()
//   ;

// export const validateURL = (value) => {
//   return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
//     value
//   );
// };

export const validateURL = (urlReq: string): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let url: any;
  try {
    url = new URL(urlReq);
  } catch (e) {
    return false;
  }
  return url.protocol === 'http:' || url.protocol === 'https:';
};

export const numberWithCommas = (x) => {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatPaiseToRupeesWithCommas = (paise) => {
  if ([null, undefined, ''].includes(paise)) return '';
  const rupees = String(paiseToRupees(paise));
  const [integerPart = 0, decimalPart = 0] = rupees.split('.');
  return `${numberWithCommas(integerPart)}.${decimalPart}`;
};

export function formatToRupees(amount = 0) {
  return "₹" + amount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

export function formatToRupeesOds(amount="0") {
  const numericAmount = parseFloat(amount);
  
  return "₹" + numericAmount.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
export const getTimeKey = (key) => {
  // return Number(key) <= 11
  //   ? `${key}:00 AM`
  //   : `${
  //       key % 12 === 0 ? 12 : key % 12 < 10 ? `0${key % 12}` : key % 12
  //     }:00 PM`;
  return `${Number(key)}:00`;
};

export const copyToClipboard = (str: string) => {
  const el = document.createElement('textarea');
  el.value = str;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

export const getPrevMidNightTimeStamp = (timeStamp: number): number =>
  new Date(timeStamp).setHours(0, 0, 0, 0);

export const getNextMidNightTimeStamp = (timeStamp: number): number =>
  new Date(timeStamp).setHours(23, 59, 59, 999);

export const formatToIST = (date: Date): string =>
  format(date, 'yyyy-MM-dd HH:mm:ss.SSS');

export const formatDate = (date) => {
  return format(new Date(date), 'yyyy-MM-dd hh:mm:ss');
};

export const formatDDMMYYY = (date) => {
  return format(new Date(date), 'dd/MM/yyyy');
};

export const formatDateWithMonthName = (date: Date | string) => {
  return format(new Date(date), 'd MMM, yyyy');
};

export const formatToIst = (date: Date): string =>
  format(date, 'yyyy-MM-dd HH:mm:ss');

export const convertToUTC = (dateString) => {
  const date = new Date(dateString);

  const utcDateString = date.toISOString();

  return utcDateString;
};

export const convertToIST = (date: Date): string => {
  const istDate = new Date(date);
  istDate.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0); // Ensure exact time remains
  return `${istDate.getFullYear()}-${String(istDate.getMonth() + 1).padStart(2, "0")}-${String(
    istDate.getDate()
  ).padStart(2, "0")} ${String(istDate.getHours()).padStart(2, "0")}:${String(
    istDate.getMinutes()
  ).padStart(2, "0")}:${String(istDate.getSeconds()).padStart(2, "0")}`;
};

export const formatDateTime = (dateInMs: number): string => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Convert milliseconds to IST (UTC+5:30)
  const date = new Date(dateInMs);
  const istOffset = 5.5 * 60 * 60 * 1000; // IST offset in milliseconds
  const istDate = new Date(date.getTime() + istOffset);
  
  const day = istDate.getUTCDate();
  const month = months[istDate.getUTCMonth()];
  const year = istDate.getUTCFullYear();
  
  return `${day} ${month} ${year}`;
};

export const validateMultipleURL = (str: string): boolean => {
  const multipleURLs = str.split(',');
  const checkValid = multipleURLs.find((url) => !validateURL(url));
  return !checkValid;
};

export const getDateRangeTitle = (
  startDate: Date | string,
  endDate: Date | string
): string => {
  let dateTitle = '';
  if (isToday(new Date(startDate)) && isToday(new Date(endDate))) {
    dateTitle = 'Today';
  } else if (
    isYesterday(new Date(startDate)) &&
    isYesterday(new Date(endDate))
  ) {
    dateTitle = 'Yesterday';
  } else if (
    isFirstDayOfMonth(new Date(startDate)) &&
    new Date(endDate).getTime() ===
    getNextMidNightTimeStamp(new Date().getTime()) &&
    getMonth(new Date(startDate)) === getMonth(new Date())
  ) {
    dateTitle = 'This Month';
  } else if (
    isFirstDayOfMonth(new Date(startDate)) &&
    isLastDayOfMonth(new Date(endDate)) &&
    getMonth(new Date(startDate)) + 1 === getMonth(new Date()) &&
    getYear(new Date(startDate)) === getYear(new Date())
  ) {
    dateTitle = 'Last Month';
  } else if (
    isSunday(new Date(startDate)) &&
    new Date(endDate).getTime() ===
    getNextMidNightTimeStamp(new Date().getTime()) &&
    isThisWeek(new Date(startDate))
  ) {
    dateTitle = 'This Week';
  } else if (
    isSunday(new Date(startDate)) &&
    isSaturday(new Date(endDate)) &&
    addDays(new Date(startDate), 7).toString() ===
    startOfWeek(new Date()).toString()
  ) {
    dateTitle = 'Last Week';
  } else if (
    isToday(new Date(endDate)) &&
    format(
      new Date(
        getNextMidNightTimeStamp(addDays(new Date(startDate), 7).getTime())
      ),
      'dd/MM/yyyy'
    ).toString() === format(new Date(endDate), 'dd/MM/yyyy').toString()
  ) {
    dateTitle = 'Last 7 Days';
  }
  return dateTitle;
};

export const checkPermissions = (permission: permissionEnum): boolean => {
  const userPermissions = store.getState().loginReducer?.permissionState
    ? store.getState().loginReducer?.permissionState
    : {};
  if (userPermissions[permission]) {
    return true;
  } else {
    return false;
  }
};

export const useFilteredSidebarOptions = (options: Options[]) => {
  const userPermissions = store.getState().loginReducer?.permissionState
    ? store.getState().loginReducer?.permissionState
    : {};

  // const isAdapter = store.getState().loginReducer?.loginState?.isAdapter;

  return options.filter((item) => {
    if (item.value === UiRoutes.MERCHANT_REPORT) {
      return !!userPermissions['GENERATE_REPORTS']; 
    }
    if(item.value === UiRoutes.PARTNER_REPORTS){
      return !!userPermissions['GENERATE_PARTNER_REPORTS'];
    }
    // if(item.value === UiRoutes.EDGE_REFUNDS){
    //   return !isAdapter;
    // }
    return true; 
  });
};

export const verifyPermissions = (): boolean => {
  const isAdmin = getIsAdminFromStore();
  if (isAdmin) {
    return checkPermissions("UPDATE_DELETE_ADMIN_USER");
  } else {
    return checkPermissions("UPDATE_DELETE_USER");
  }
};

export const checkRole = (checkListRoles: string[]): boolean => {
  const userRole: string =
    store.getState().loginReducer?.roleState?.roleName || '';
  if (checkListRoles.includes(userRole)) {
    return true;
  } else {
    return false;
  }
};

export const getBase64Data = (payload) => {
  const buff = new Buffer(JSON.stringify(payload));
  const base64data = buff.toString('base64');
  return base64data;
};

export const getClientSecretKeyFromStore = (): string => {
  return store.getState().loginReducer?.clientSecretKeyState?.data;
};

export const redirectToUrl = (url: string): void => {
  const tempLink = document.createElement('a');
  tempLink.href = url;
  tempLink.setAttribute('id', 'redirection-url-link');
  tempLink.click();
  tempLink.remove();
};

export const timerDisplay = (remainingSeconds: number) => {
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds - minutes * 60;
  const displaySeconds = seconds < 10 ? `0${seconds}` : `${seconds}`;
  return `${minutes}:` + displaySeconds;
};

export const dateRangeUpToToday = (dayasUpToToday = 7) => {
  return {
    startDate: subDays(
      new Date(getPrevMidNightTimeStamp(new Date().getTime())),
      dayasUpToToday
    ),
    endDate: new Date(getNextMidNightTimeStamp(new Date().getTime())),
    key: 'selection',
  };
};

export const getKeyByValue = (object, value) => {
  return Object.keys(object).find((key) => object[key] === value);
};
export const paiseToRupees = (paise) => {
  const paiseInNumber = !paise ? 0 : Number(paise);
  return paiseInNumber ? (parseFloat(paise) / 100).toFixed(2) : paiseInNumber;
};
export const rupeesToPaise = (rupees) => {
  const rupeesInNumber = !rupees ? 0 : Number(rupees);
  return (rupeesInNumber * 100).toFixed(2);
};

export const convertFileToBase64 = async (file) => {
  return await new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = function () {
      return resolve({
        name: file.name,
        size: file.size,
        type: file.type,
        data: fileReader.result,
      });
    };
  });
};

export const isEMITxn = (paymentModeId: number) => {
  return (
    Number(paymentModeId) === EMI_PAYMENT_MODES.EMI ||
    Number(paymentModeId) === EMI_PAYMENT_MODES.DEBIT_EMI
  );
};
export const isValidDate = (date: any): boolean => {
  return moment(date).isValid();
};

export const toCapitalizeArray = (arr: string[]): string => {
  let finalString = '';
  if (arr && arr.length > 0) {
    for (const str of arr) {
      finalString += `${toCapitalize(str)} `;
    }
    return finalString;
  }
  return 'NA';
};

export const isLiveDomain = (): boolean => {
  return (
    process.env.REACT_APP_BUILD_ENV === 'prod' ||
    process.env.REACT_APP_BUILD_ENV === 'qa'
  );
};

export const isPayoutEnabled = () => {
  const mappedProducts =
    store?.getState()?.loginReducer?.loginState?.mappedProducts;
  const payoutActive = mappedProducts.find(
    (product: Product) => product?.id === PAYOUT_ID
    && product?.status === 2
  );
  return Boolean(payoutActive?.id);
};

export const isSubscriptionEnabled = () => {
  const mappedProducts =
    store?.getState()?.loginReducer?.loginState?.mappedProducts;
  const subscriptionActive = mappedProducts.find(
    (product: Product) => product?.id === SUBSCRIPTION_ID
    && product?.status === 2
  );
  return Boolean(subscriptionActive?.id);
};

export const getSelectedProduct = () => {
  const selectedProduct: Product =
    store?.getState()?.loginReducer?.loginState?.selectedProduct;
  return selectedProduct?.product ?? PRODUCTS[selectedProduct?.name ?? ''];
};

export const getMerchantVersion = () => {
  const merchantVersion =
    store?.getState()?.loginReducer?.loginState?.merchantVersion;
  return merchantVersion;
};

export const getIsTransactingMerchant = (): boolean =>{
  const isTransactingMerchant = store?.getState()?.loginReducer?.loginState?.isTransactingMerchant;
  console.log("isTransactingMerchant", isTransactingMerchant);
  return isTransactingMerchant;
};

export const getSelectedProductFromPath = (
  path = '',
  mappedProducts: Product[] = []
): any => {
  // Try to find by displayName first
  let selectedProduct = mappedProducts.find(
    (item) => item?.displayName === EnumProduct[path]
  );
  
  // If not found, try matching by ID for known paths
  if (!selectedProduct) {
    const productId = PATH_TO_PRODUCT_ID_MAP[path];
    if (productId) {
      selectedProduct = mappedProducts.find((item) => item?.id === productId);
    }
  }
  
  const result = selectedProduct || mappedProducts[0];
  
  // Ensure product field is set based on ID
  if (result && !result.product) {
    result.product = PRODUCT_ID_TO_NAME_MAP[result.id];
  }
  
  return result;
};

export const getDefaultSelectedProduct = (mappedProducts: Product[] = []) => {
  const selectedProduct = mappedProducts.find(
    (item) => item?.displayName === EnumProduct.payout
  );

  const result = selectedProduct || mappedProducts[0];
  
  // Ensure product field is set based on ID
  if (result && !result.product) {
    result.product = PRODUCT_ID_TO_NAME_MAP[result.id];
  }
  
  return result;
};

export const getDefaultPathFromMerchant = (mappedProducts: Product[] = []) => {
  const isPayoutPresent = mappedProducts.some(
    (item) => item?.displayName === EnumProduct.payout
  );

  return isPayoutPresent ? UiRoutes.INDIVIDUAL_PAYOUTS : UiRoutes.OVERVIEW;
};

export const getPayoutMid = () => {
  const mappedProducts =
    store?.getState()?.loginReducer?.loginState?.mappedProducts;

  const payout = mappedProducts.find((item) => item.product_name === 'PAYOUT');

  return payout?.mid;
};

export const generateUuid = () => {
  return uuidv4();
};

export const getUniqueKeyFromMerchantName = (merchantName: string) => {
  const uniqueKey = merchantName.substring(0, 3).toLowerCase();
  return uniqueKey.trim();
};

export function colorToHsl(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!result) {
    throw new Error("Invalid HEX color.");
  }

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let l = (max + min) / 2;

  l = Math.round(l * 100);

  return l;
}


export function hexToRgb(hex) {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse hex values to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return { r, g, b };
}

export const urlRegex = /^(https?:\/\/)?([\w\d-]+\.)+[\w]{2,}(\/.+)?$/i;

export const mapVideoKycStatusToBoxType = (kycCode: number): 'USER_VERIFICATION' | 'VIDEO_KYC_RETRY' | 'VIDEO_KYC_PROGRESS' | null => {
  if (kycCode === 4 || kycCode === 5) return 'USER_VERIFICATION';
  if ([2, 3, 6, 7].includes(kycCode)) return 'VIDEO_KYC_RETRY';
  if (kycCode === 1) return 'VIDEO_KYC_PROGRESS';
  return 'USER_VERIFICATION';
};

export const mapVideoKycCodeToPropsStatus = (kycCode: number): 'REJECTED' | 'PENDING' | 'REJECTED_OUT_OF_HOURS' => {
  if ([2, 3, 6, 7].includes(kycCode)) return 'REJECTED';
  if (kycCode === 4) return 'PENDING';
  return 'REJECTED';
};

export  const getStepMerchantDetails = (
  stepperDetails: any,
  stepName: string
): any | undefined => {
  return stepperDetails?.data?.lead_progress?.find(
    (step) => step?.step_name === stepName
  )?.merchant_details;
};


export const getStepDetails = (  stepperDetails: any, stepName: string): any | undefined =>{
  const steps = stepperDetails?.data?.lead_progress;
  if (!Array.isArray(steps)) return undefined;

  return [...steps].reverse().find(step => step?.step_name === stepName);
};

export const buildRequestPayload = (reqPayload, publicPayload) => {
  if (!publicPayload) return reqPayload;
  
  return {
    ...reqPayload,
    token: publicPayload.token || "",
    token_type: publicPayload.token_type || "",
    emailId: publicPayload.emailId || "",
    merchantId: publicPayload.merchantId || ""
  };
};

export const buildGetRequestPayload = (publicPayload) => {
  if (!publicPayload) return undefined;
  
  return { 
     token: publicPayload?.token || "",
    token_type: publicPayload?.token_type || "",
    emailId: publicPayload?.emailId || "",
    merchantId: publicPayload?.merchantId || "" };
};

export const downloadBase64AsPdf = (base64Data: string, fileName = 'document.pdf') => {
    try {
      // Handle different base64 formats
      let base64String = base64Data;
      
      // Remove data URI prefix if present (data:application/pdf;base64,)
      if (base64String.includes('base64,')) {
        base64String = base64String.split('base64,')[1];
      } else if (base64String.includes(',')) {
        base64String = base64String.split(',')[1];
      }
      
      // Remove any whitespace or newlines
      base64String = base64String.replace(/\s/g, '');
      
      // Convert base64 to binary
      const binaryString = window.atob(base64String);
      const uint8Array = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        uint8Array[i] = binaryString.charCodeAt(i);
      }
      
      // Create blob
      const pdfBlob = new Blob([uint8Array], { type: 'application/pdf' });
      
      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 100);
      
      notify({
        type: 'success',
        message: 'PDF Downloaded',
        description: `File ${fileName} has been downloaded successfully.`,
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      notify({
        type: 'error',
        message: 'Failed to download PDF',
        description: 'There was an error processing the PDF file. Please check the console for details.',
      });
    }
  };

export const shouldUseUploadDocuments = (payload: any) => {
  if (!payload) return true;
  const keys = Object.keys(payload);
  if (keys.length === 0) return true;
  if (keys.length === 1 && keys[0] === 'is_rectification' && payload.is_rectification === true) return true;
  return false;
};

/**
 * Validates and sanitizes input text to prevent XSS attacks and malicious content
 * @param value - The input string to validate
 * @returns Object with validation result and error message if any
 */
export const validateAndSanitizeInput = (value: string): {
  isValid: boolean;
  errorMessage?: string;
  sanitizedValue?: string;
} => {
  if (!value || typeof value !== 'string') {
    return { isValid: false, errorMessage: 'Input must be a valid string.' };
  }

  // Check for script tags
  const scriptTagPattern = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
  if (scriptTagPattern.test(value)) {
    return { isValid: false, errorMessage: 'Script tags are not allowed.' };
  }

  // Check for dangerous JavaScript patterns
  const dangerousPatterns = /javascript:|onerror=|onload=|onclick=|onmouseover=|onmouseout=|onfocus=|onblur=/gi;
  if (dangerousPatterns.test(value)) {
    return { isValid: false, errorMessage: 'JavaScript code and event handlers are not allowed.' };
  }

  // Check for HTML tags
  const htmlTagPattern = /<[^>]*>/g;
  if (htmlTagPattern.test(value)) {
    return { isValid: false, errorMessage: 'HTML tags are not allowed.' };
  }

  // Basic sanitization - trim whitespace
  const sanitizedValue = value.trim();

  return {
    isValid: true,
    sanitizedValue
  };
};
